(function (global) {
  'use strict';

  function createDefaultPricingEngineConfig() {
    return {
      schemaVersion: '1.0.0',
      status: 'pending-approval',
      currency: 'AUD',
      defaultPricingContext: 'retailPriceAUD',
      products: [],
      fallbackMessaging: {
        contactTeamMessage: 'Please contact Pet Food Australia for personalised pricing guidance.'
      },
      metadata: {
        owner: 'Nutrition Team',
        status: 'Pending approval',
        lastUpdated: '2026-07-16'
      }
    };
  }

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isPositiveNumber(value) {
    return typeof value === 'number' && value > 0;
  }

  function round2(value) {
    return Math.round(value * 100) / 100;
  }

  function getPricingEngine(config) {
    if (!config) {
      return null;
    }

    if (config.pricingEngine) {
      return config.pricingEngine;
    }

    if (config.schemaVersion && Array.isArray(config.products)) {
      return config;
    }

    return null;
  }

  function getContactTeamMessage(pricingEngine) {
    return pricingEngine && pricingEngine.fallbackMessaging && pricingEngine.fallbackMessaging.contactTeamMessage
      ? pricingEngine.fallbackMessaging.contactTeamMessage
      : 'Please contact Pet Food Australia for personalised pricing guidance.';
  }

  function createFallbackPricingPlan(input, pricingEngine, reason) {
    const contactTeamMessage = getContactTeamMessage(pricingEngine);
    const notes = reason ? [reason, contactTeamMessage] : [contactTeamMessage];

    return {
      status: 'contact-team',
      recipeId: input && input.recipeId ? input.recipeId : null,
      lifecycleStage: input && input.lifecycleStage ? input.lifecycleStage : null,
      gramsPerDay: null,
      minimumGramsPerDay: null,
      maximumGramsPerDay: null,
      variants: [],
      bestValueBag: null,
      recommendedBag: null,
      contactTeamFallback: true,
      fallbackReason: reason || 'unsupported-calculation',
      calculationNotes: notes
    };
  }

  function resolveActivePrice(variant, priceKey, market) {
    if (!variant || !isObject(variant.pricing)) {
      return null;
    }

    if (market && isObject(variant.pricing.marketOverrides) && isObject(variant.pricing.marketOverrides[market])) {
      const overrideValue = variant.pricing.marketOverrides[market][priceKey];

      if (typeof overrideValue === 'number' && overrideValue > 0) {
        return overrideValue;
      }
    }

    const baseValue = variant.pricing[priceKey];

    if (typeof baseValue === 'number' && baseValue > 0) {
      return baseValue;
    }

    return null;
  }

  function resolvePricingContext(pricingEngine, pricingContext) {
    const defaultKey = pricingEngine && pricingEngine.defaultPricingContext
      ? pricingEngine.defaultPricingContext
      : 'retailPriceAUD';

    if (!pricingContext || !isObject(pricingContext)) {
      return { priceKey: defaultKey, market: null };
    }

    return {
      priceKey: (typeof pricingContext.priceKey === 'string' && pricingContext.priceKey)
        ? pricingContext.priceKey
        : defaultKey,
      market: (typeof pricingContext.market === 'string' && pricingContext.market)
        ? pricingContext.market
        : null
    };
  }

  function findProduct(products, recipeId) {
    if (!Array.isArray(products) || !recipeId) {
      return null;
    }

    return products.find(function (p) { return p && p.productId === recipeId; }) || null;
  }

  function buildAdultVariantResult(variant, gramsPerDay, priceKey, market, freeShippingThreshold, smallestCostPerKg) {
    const activePrice = resolveActivePrice(variant, priceKey, market);
    const sizeGrams = variant.sizeGrams;
    const isAvailable = variant.isAvailable !== false;

    if (activePrice === null) {
      return {
        variantId: variant.variantId,
        sizeGrams: sizeGrams,
        priceAUD: null,
        subscriptionPriceAUD: null,
        isAvailable: false,
        bagDurationDays: null,
        costPerDay: null,
        costPerWeek: null,
        costPerMonth: null,
        costPerKg: null,
        subscriptionSavings: null,
        subscriptionCostPerDay: null,
        // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
        savingsVsSmallestBag: null,
        qualifiesForFreeShipping: null
      };
    }

    const bagDurationDays = sizeGrams / gramsPerDay;
    const rawCostPerDay = activePrice / bagDurationDays;
    const costPerDay = round2(rawCostPerDay);
    const costPerWeek = round2(rawCostPerDay * 7);
    const costPerMonth = round2(rawCostPerDay * 30);
    const costPerKg = round2(activePrice / (sizeGrams / 1000));

    const retailPrice = resolveActivePrice(variant, 'retailPriceAUD', market);
    const subPrice = resolveActivePrice(variant, 'subscriptionPriceAUD', market);
    const subscriptionSavings = (retailPrice !== null && subPrice !== null) ? round2(retailPrice - subPrice) : null;
    const subscriptionCostPerDay = subPrice !== null ? round2(subPrice / bagDurationDays) : null;

    const qualifiesForFreeShipping = freeShippingThreshold !== null ? activePrice >= freeShippingThreshold : null;
    // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
    const savingsVsSmallestBag = smallestCostPerKg !== null
      ? round2((smallestCostPerKg - costPerKg) * (sizeGrams / 1000))
      : null;

    return {
      variantId: variant.variantId,
      sizeGrams: sizeGrams,
      priceAUD: activePrice,
      subscriptionPriceAUD: subPrice,
      isAvailable: isAvailable,
      bagDurationDays: bagDurationDays,
      costPerDay: costPerDay,
      costPerWeek: costPerWeek,
      costPerMonth: costPerMonth,
      costPerKg: costPerKg,
      subscriptionSavings: subscriptionSavings,
      subscriptionCostPerDay: subscriptionCostPerDay,
      // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
      savingsVsSmallestBag: savingsVsSmallestBag,
      qualifiesForFreeShipping: qualifiesForFreeShipping
    };
  }

  function buildPuppyVariantResult(variant, minimumGramsPerDay, maximumGramsPerDay, priceKey, market, freeShippingThreshold, smallestCostPerKg) {
    const activePrice = resolveActivePrice(variant, priceKey, market);
    const sizeGrams = variant.sizeGrams;
    const isAvailable = variant.isAvailable !== false;

    if (activePrice === null) {
      return {
        variantId: variant.variantId,
        sizeGrams: sizeGrams,
        priceAUD: null,
        subscriptionPriceAUD: null,
        isAvailable: false,
        minimumBagDurationDays: null,
        maximumBagDurationDays: null,
        minimumCostPerDay: null,
        maximumCostPerDay: null,
        minimumCostPerMonth: null,
        maximumCostPerMonth: null,
        costPerKg: null,
        subscriptionSavings: null,
        // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
        savingsVsSmallestBag: null,
        qualifiesForFreeShipping: null
      };
    }

    const minimumBagDurationDays = sizeGrams / maximumGramsPerDay;
    const maximumBagDurationDays = sizeGrams / minimumGramsPerDay;
    const rawMinCostPerDay = activePrice / maximumBagDurationDays;
    const rawMaxCostPerDay = activePrice / minimumBagDurationDays;
    const minimumCostPerDay = round2(rawMinCostPerDay);
    const maximumCostPerDay = round2(rawMaxCostPerDay);
    const minimumCostPerMonth = round2(rawMinCostPerDay * 30);
    const maximumCostPerMonth = round2(rawMaxCostPerDay * 30);
    const costPerKg = round2(activePrice / (sizeGrams / 1000));

    const retailPrice = resolveActivePrice(variant, 'retailPriceAUD', market);
    const subPrice = resolveActivePrice(variant, 'subscriptionPriceAUD', market);
    const subscriptionSavings = (retailPrice !== null && subPrice !== null) ? round2(retailPrice - subPrice) : null;

    const qualifiesForFreeShipping = freeShippingThreshold !== null ? activePrice >= freeShippingThreshold : null;
    // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
    const savingsVsSmallestBag = smallestCostPerKg !== null
      ? round2((smallestCostPerKg - costPerKg) * (sizeGrams / 1000))
      : null;

    return {
      variantId: variant.variantId,
      sizeGrams: sizeGrams,
      priceAUD: activePrice,
      subscriptionPriceAUD: subPrice,
      isAvailable: isAvailable,
      minimumBagDurationDays: minimumBagDurationDays,
      maximumBagDurationDays: maximumBagDurationDays,
      minimumCostPerDay: minimumCostPerDay,
      maximumCostPerDay: maximumCostPerDay,
      minimumCostPerMonth: minimumCostPerMonth,
      maximumCostPerMonth: maximumCostPerMonth,
      costPerKg: costPerKg,
      subscriptionSavings: subscriptionSavings,
      // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
      savingsVsSmallestBag: savingsVsSmallestBag,
      qualifiesForFreeShipping: qualifiesForFreeShipping
    };
  }

  function enrichVariantsWithComparisonSavings(calculatedVariants, productVariantsConfig, priceKey, market) {
    const configByVariantId = Object.create(null);
    if (Array.isArray(productVariantsConfig)) {
      productVariantsConfig.forEach(function (configVariant) {
        if (configVariant && configVariant.variantId) {
          configByVariantId[configVariant.variantId] = configVariant;
        }
      });
    }

    return calculatedVariants.map(function (variant) {
      const configVariant = configByVariantId[variant.variantId];
      let savingsVsEquivalentIndividualBags = null;
      let savingsVsEquivalentBundle = null;

      const activePrice = variant.priceAUD;

      if (activePrice !== null && configVariant) {
        const indivComp = configVariant.savingsComparisonIndividual;
        if (indivComp && typeof indivComp.variantId === 'string' && indivComp.variantId && isPositiveNumber(indivComp.quantity)) {
          const compConfig = configByVariantId[indivComp.variantId];
          if (compConfig) {
            const compPrice = resolveActivePrice(compConfig, priceKey, market);
            if (compPrice !== null) {
              savingsVsEquivalentIndividualBags = round2(indivComp.quantity * compPrice - activePrice);
            }
          }
        }

        const bundleComp = configVariant.savingsComparisonBundle;
        if (bundleComp && typeof bundleComp.variantId === 'string' && bundleComp.variantId) {
          const compConfig = configByVariantId[bundleComp.variantId];
          if (compConfig) {
            const compPrice = resolveActivePrice(compConfig, priceKey, market);
            if (compPrice !== null) {
              savingsVsEquivalentBundle = round2(compPrice - activePrice);
            }
          }
        }
      }

      return Object.assign({}, variant, {
        savingsVsEquivalentIndividualBags: savingsVsEquivalentIndividualBags,
        savingsVsEquivalentBundle: savingsVsEquivalentBundle,
        savingsCalculationMethod: {
          // @deprecated – retained for backward compatibility; superseded by the explicit savings fields
          savingsVsSmallestBag: 'per-kg-normalised',
          savingsVsEquivalentIndividualBags: 'equivalent-individual-bags',
          savingsVsEquivalentBundle: 'equivalent-bundle',
          subscriptionSavings: 'subscription-discount'
        }
      });
    });
  }

  function validatePricingEngineConfig(config) {
    const errors = [];
    const pricingEngine = getPricingEngine(config);

    if (!pricingEngine) {
      return { isValid: false, errors: ['pricingEngine configuration is missing.'] };
    }

    if (pricingEngine.schemaVersion !== '1.0.0') {
      errors.push('pricingEngine.schemaVersion must be 1.0.0.');
    }

    if (!pricingEngine.status || typeof pricingEngine.status !== 'string') {
      errors.push('pricingEngine.status is required.');
    }

    if (pricingEngine.defaultPricingContext !== undefined &&
        (typeof pricingEngine.defaultPricingContext !== 'string' || !pricingEngine.defaultPricingContext)) {
      errors.push('pricingEngine.defaultPricingContext must be a non-empty string.');
    }

    if (pricingEngine.freeShippingThresholdAUD !== undefined && !isPositiveNumber(pricingEngine.freeShippingThresholdAUD)) {
      errors.push('pricingEngine.freeShippingThresholdAUD must be a positive number.');
    }

    if (pricingEngine.recommendedBagMinimumDurationDays !== undefined &&
        !isPositiveNumber(pricingEngine.recommendedBagMinimumDurationDays)) {
      errors.push('pricingEngine.recommendedBagMinimumDurationDays must be a positive number.');
    }

    const isPending = pricingEngine.status && /pending/i.test(pricingEngine.status);

    if (!Array.isArray(pricingEngine.products)) {
      errors.push('pricingEngine.products must be an array.');
    } else {
      if (!isPending && pricingEngine.products.length === 0) {
        errors.push('pricingEngine.products must not be empty when status is not pending.');
      }

      const productIds = new Set();

      pricingEngine.products.forEach(function (product) {
        if (!isObject(product)) {
          errors.push('pricingEngine.products entries must be objects.');
          return;
        }

        if (!product.productId || typeof product.productId !== 'string') {
          errors.push('pricingEngine.products entries must include a string productId.');
        } else if (productIds.has(product.productId)) {
          errors.push('pricingEngine.products must not contain duplicate productId values.');
        } else {
          productIds.add(product.productId);
        }

        if (!Array.isArray(product.variants)) {
          errors.push('pricingEngine.products entries must include a variants array.');
          return;
        }

        if (!isPending && product.variants.length === 0) {
          errors.push('pricingEngine.products variants must not be empty when status is not pending.');
        }

        const variantIds = new Set();

        product.variants.forEach(function (variant) {
          if (!isObject(variant)) {
            errors.push('pricingEngine.products variants must be objects.');
            return;
          }

          if (!variant.variantId || typeof variant.variantId !== 'string') {
            errors.push('pricingEngine.products variants must include a string variantId.');
          } else if (variantIds.has(variant.variantId)) {
            errors.push('pricingEngine.products variants must not contain duplicate variantId values within a product.');
          } else {
            variantIds.add(variant.variantId);
          }

          if (!isPositiveNumber(variant.sizeGrams)) {
            errors.push('pricingEngine.products variants must include a positive numeric sizeGrams.');
          }

          if (variant.isAvailable !== undefined && typeof variant.isAvailable !== 'boolean') {
            errors.push('pricingEngine.products variants isAvailable must be a boolean.');
          }

          if (!isObject(variant.pricing)) {
            errors.push('pricingEngine.products variants must include a pricing object.');
            return;
          }

          if (!isPositiveNumber(variant.pricing.retailPriceAUD)) {
            errors.push('pricingEngine.products variants pricing.retailPriceAUD must be a positive number.');
          }

          if (variant.pricing.subscriptionPriceAUD !== undefined && variant.pricing.subscriptionPriceAUD !== null) {
            if (!isPositiveNumber(variant.pricing.subscriptionPriceAUD)) {
              errors.push('pricingEngine.products variants pricing.subscriptionPriceAUD must be a positive number.');
            } else if (isPositiveNumber(variant.pricing.retailPriceAUD) &&
                       variant.pricing.subscriptionPriceAUD > variant.pricing.retailPriceAUD) {
              errors.push('pricingEngine.products variants pricing.subscriptionPriceAUD must not exceed retailPriceAUD.');
            }
          }

          if (variant.pricing.promotionalPriceAUD !== undefined && variant.pricing.promotionalPriceAUD !== null) {
            if (!isPositiveNumber(variant.pricing.promotionalPriceAUD)) {
              errors.push('pricingEngine.products variants pricing.promotionalPriceAUD must be a positive number.');
            }
          }

          if (variant.pricing.wholesalePriceAUD !== undefined && variant.pricing.wholesalePriceAUD !== null) {
            if (!isPositiveNumber(variant.pricing.wholesalePriceAUD)) {
              errors.push('pricingEngine.products variants pricing.wholesalePriceAUD must be a positive number.');
            }
          }

          if (variant.pricing.marketOverrides !== undefined) {
            if (!isObject(variant.pricing.marketOverrides)) {
              errors.push('pricingEngine.products variants pricing.marketOverrides must be an object.');
            } else {
              Object.keys(variant.pricing.marketOverrides).forEach(function (marketCode) {
                const override = variant.pricing.marketOverrides[marketCode];

                if (!isObject(override)) {
                  errors.push('pricingEngine.products variants pricing.marketOverrides entries must be objects.');
                  return;
                }

                Object.keys(override).forEach(function (priceKey) {
                  const val = override[priceKey];

                  if (val !== null && val !== undefined && !isPositiveNumber(val)) {
                    errors.push('pricingEngine.products variants pricing.marketOverrides price values must be positive numbers.');
                  }
                });
              });
            }
          }
        });
      });
    }

    if (pricingEngine.fallbackMessaging !== undefined && !isObject(pricingEngine.fallbackMessaging)) {
      errors.push('pricingEngine.fallbackMessaging must be an object.');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  function createPlaceholderPricingPlan(input) {
    return {
      status: 'pending-approval',
      recipeId: input && input.recipeId ? input.recipeId : null,
      lifecycleStage: input && input.lifecycleStage ? input.lifecycleStage : null,
      gramsPerDay: null,
      minimumGramsPerDay: null,
      maximumGramsPerDay: null,
      variants: [],
      bestValueBag: null,
      recommendedBag: null,
      contactTeamFallback: true,
      fallbackReason: null,
      calculationNotes: [
        'Pricing data is pending approval.',
        'No calculated pricing results are available yet.'
      ]
    };
  }

  function buildPricingPlan(feedingPlan, input, config, pricingContext) {
    const validation = validatePricingEngineConfig(config || {});
    const pricingEngine = getPricingEngine(config || {});

    const safeInput = {
      recipeId: (feedingPlan && feedingPlan.recipeId) || (input && input.recipeId) || null,
      lifecycleStage: (feedingPlan && feedingPlan.lifecycleStage) || (input && input.lifecycleStage) || null
    };

    if (!validation.isValid) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'invalid-pricing-engine-configuration');
    }

    if (!feedingPlan || feedingPlan.status !== 'calculated') {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'feeding-plan-not-available');
    }

    const recipeId = feedingPlan.recipeId;
    const lifecycleStage = feedingPlan.lifecycleStage;

    if (!recipeId || !lifecycleStage) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'missing-recipe-or-lifecycle');
    }

    if (lifecycleStage === 'adult' && !isPositiveNumber(feedingPlan.gramsPerDay)) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'missing-feeding-inputs');
    }

    if (lifecycleStage === 'puppy' &&
        (!isPositiveNumber(feedingPlan.minimumGramsPerDay) || !isPositiveNumber(feedingPlan.maximumGramsPerDay))) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'missing-feeding-inputs');
    }

    const product = findProduct(pricingEngine.products, recipeId);

    if (!product) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'product-not-found');
    }

    const context = resolvePricingContext(pricingEngine, pricingContext);
    const priceKey = context.priceKey;
    const market = context.market;

    const freeShippingThreshold = isPositiveNumber(pricingEngine.freeShippingThresholdAUD)
      ? pricingEngine.freeShippingThresholdAUD
      : null;

    const recommendedBagMinDays = isPositiveNumber(pricingEngine.recommendedBagMinimumDurationDays)
      ? pricingEngine.recommendedBagMinimumDurationDays
      : null;

    const availableVariants = product.variants.filter(function (v) {
      return v && v.isAvailable !== false;
    });

    if (!availableVariants.length) {
      return createFallbackPricingPlan(safeInput, pricingEngine, 'no-available-variants');
    }

    const sortedAvailableBySize = availableVariants.slice().sort(function (a, b) {
      return a.sizeGrams - b.sizeGrams;
    });

    const smallestVariant = sortedAvailableBySize[0];
    const smallestActivePrice = resolveActivePrice(smallestVariant, priceKey, market);
    const smallestCostPerKg = smallestActivePrice !== null
      ? smallestActivePrice / (smallestVariant.sizeGrams / 1000)
      : null;

    let calculatedVariants;

    if (lifecycleStage === 'adult') {
      calculatedVariants = product.variants.map(function (variant) {
        return buildAdultVariantResult(variant, feedingPlan.gramsPerDay, priceKey, market, freeShippingThreshold, smallestCostPerKg);
      });
    } else {
      calculatedVariants = product.variants.map(function (variant) {
        return buildPuppyVariantResult(
          variant,
          feedingPlan.minimumGramsPerDay,
          feedingPlan.maximumGramsPerDay,
          priceKey,
          market,
          freeShippingThreshold,
          smallestCostPerKg
        );
      });
    }

    calculatedVariants = enrichVariantsWithComparisonSavings(calculatedVariants, product.variants, priceKey, market);

    const pricedAvailableVariants = calculatedVariants.filter(function (v) {
      return v.isAvailable !== false && v.costPerKg !== null;
    });

    let bestValueBag = null;
    let recommendedBag = null;

    if (pricedAvailableVariants.length) {
      const sortedByValue = pricedAvailableVariants.slice().sort(function (a, b) {
        return a.costPerKg - b.costPerKg;
      });
      bestValueBag = sortedByValue[0].variantId;

      if (recommendedBagMinDays !== null) {
        let qualifyingVariants;

        if (lifecycleStage === 'adult') {
          qualifyingVariants = pricedAvailableVariants.filter(function (v) {
            return v.bagDurationDays !== null && v.bagDurationDays >= recommendedBagMinDays;
          });
        } else {
          qualifyingVariants = pricedAvailableVariants.filter(function (v) {
            return v.minimumBagDurationDays !== null && v.minimumBagDurationDays >= recommendedBagMinDays;
          });
        }

        if (qualifyingVariants.length) {
          const sortedQualifying = qualifyingVariants.slice().sort(function (a, b) {
            return a.costPerKg - b.costPerKg;
          });
          recommendedBag = sortedQualifying[0].variantId;
        } else {
          recommendedBag = bestValueBag;
        }
      } else {
        recommendedBag = bestValueBag;
      }
    }

    if (lifecycleStage === 'adult') {
      return {
        status: 'calculated',
        recipeId: recipeId,
        lifecycleStage: lifecycleStage,
        gramsPerDay: feedingPlan.gramsPerDay,
        variants: calculatedVariants,
        bestValueBag: bestValueBag,
        recommendedBag: recommendedBag,
        contactTeamFallback: false,
        fallbackReason: null,
        calculationNotes: ['Calculated from the approved pricing configuration.']
      };
    }

    return {
      status: 'calculated',
      recipeId: recipeId,
      lifecycleStage: lifecycleStage,
      minimumGramsPerDay: feedingPlan.minimumGramsPerDay,
      maximumGramsPerDay: feedingPlan.maximumGramsPerDay,
      variants: calculatedVariants,
      bestValueBag: bestValueBag,
      recommendedBag: recommendedBag,
      contactTeamFallback: false,
      fallbackReason: null,
      calculationNotes: ['Calculated from the approved pricing configuration.']
    };
  }

  const api = {
    createDefaultPricingEngineConfig,
    validatePricingEngineConfig,
    createPlaceholderPricingPlan,
    buildPricingPlan
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.PricingEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
